import React, { FC, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import yaml from 'js-yaml';
import api from '../../api';
import MobileNavToggle from './MobileNavToggle';
import './use-case-editor.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Archetype {
  id: string;
  title: string;
  description: string;
  whenToSuggest: string;
  knowledgeNeeded: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ─── Main Component ──────────────────────────────────────────────────────────

const SuggestionArchetypeEditor: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  // Access check
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  // List view
  const [archetypes, setArchetypes] = useState<Archetype[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Detail / edit view
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Archetype | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit form state
  const [editId, setEditId] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editWhenToSuggest, setEditWhenToSuggest] = useState('');
  const [editKnowledgeNeeded, setEditKnowledgeNeeded] = useState('');
  const [editActive, setEditActive] = useState(true);

  // UX
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isNewMode, setIsNewMode] = useState(false);

  // YAML import
  const [showYamlImport, setShowYamlImport] = useState(false);
  const [yamlText, setYamlText] = useState('');
  const [yamlImporting, setYamlImporting] = useState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  const clearMessages = () => {
    setError(null);
    setSuccessMsg(null);
  };

  // ─── Fetch list ──────────────────────────────────────────────────────────

  const fetchList = useCallback(async () => {
    setListLoading(true);
    clearMessages();
    try {
      const headers = await authHeaders();
      const res = await api.get(
        `/suggestion-archetypes?includeInactive=${includeInactive}`,
        { headers },
      );
      if (res.data.success) {
        setArchetypes(res.data.archetypes);
        setHasAccess(true);
      }
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHasAccess(false);
      } else {
        setError('Failed to load suggestion archetypes');
      }
    } finally {
      setListLoading(false);
    }
  }, [authHeaders, includeInactive]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ─── Fetch detail ────────────────────────────────────────────────────────

  const fetchDetail = useCallback(
    async (id: string) => {
      setDetailLoading(true);
      clearMessages();
      try {
        const headers = await authHeaders();
        const res = await api.get(`/suggestion-archetypes/${id}`, { headers });
        if (res.data.success) {
          const a: Archetype = res.data.archetype;
          setDetail(a);
          populateForm(a);
        }
      } catch {
        setError('Failed to load archetype details');
      } finally {
        setDetailLoading(false);
      }
    },
    [authHeaders],
  );

  useEffect(() => {
    if (selectedId && !isNewMode) {
      fetchDetail(selectedId);
    }
  }, [selectedId, isNewMode, fetchDetail]);

  // ─── Populate form from Archetype ────────────────────────────────────────

  const populateForm = (a: Archetype) => {
    setEditId(a.id);
    setEditTitle(a.title);
    setEditDescription(a.description);
    setEditWhenToSuggest(a.whenToSuggest);
    setEditKnowledgeNeeded(a.knowledgeNeeded);
    setEditActive(a.active);
  };

  // ─── New archetype ─────────────────────────────────────────────────────

  const startNew = () => {
    setSelectedId(null);
    setDetail(null);
    setIsNewMode(true);
    setEditId('');
    setEditTitle('');
    setEditDescription('');
    setEditWhenToSuggest('');
    setEditKnowledgeNeeded('');
    setEditActive(true);
    clearMessages();
  };

  // ─── Select existing ──────────────────────────────────────────────────

  const selectArchetype = (id: string) => {
    setIsNewMode(false);
    setSelectedId(id);
    clearMessages();
  };

  // ─── Back to list ──────────────────────────────────────────────────────

  const backToList = () => {
    setSelectedId(null);
    setDetail(null);
    setIsNewMode(false);
    clearMessages();
    fetchList();
  };

  // ─── Save ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    clearMessages();
    if (
      !editId ||
      !editTitle ||
      !editDescription ||
      !editWhenToSuggest ||
      !editKnowledgeNeeded
    ) {
      setError(
        'All fields are required: id, title, description, when to suggest, knowledge needed.',
      );
      return;
    }

    setSaving(true);
    try {
      const headers = await authHeaders();
      const body = {
        id: editId,
        title: editTitle,
        description: editDescription,
        whenToSuggest: editWhenToSuggest,
        knowledgeNeeded: editKnowledgeNeeded,
        active: editActive,
      };

      if (isNewMode) {
        const res = await api.post('/suggestion-archetypes', body, { headers });
        if (res.data.success) {
          setSuccessMsg(`Created "${editTitle}"`);
          setIsNewMode(false);
          setSelectedId(editId);
          fetchDetail(editId);
        }
      } else {
        const res = await api.put(
          `/suggestion-archetypes/${selectedId}`,
          body,
          {
            headers,
          },
        );
        if (res.data.success) {
          setSuccessMsg(`Saved "${editTitle}"`);
          fetchDetail(editId);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Deactivate ────────────────────────────────────────────────────────

  const handleDeactivate = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Deactivate "${editTitle}"?`)) return;

    clearMessages();
    setSaving(true);
    try {
      const headers = await authHeaders();
      const res = await api.put(
        `/suggestion-archetypes/${selectedId}`,
        { active: false },
        { headers },
      );
      if (res.data.success) {
        setSuccessMsg('Archetype deactivated');
        setTimeout(backToList, 800);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Deactivate failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── YAML import ───────────────────────────────────────────────────────

  const handleYamlImport = async () => {
    clearMessages();
    if (!yamlText.trim()) {
      setError('Paste some YAML first.');
      return;
    }

    let parsed: any;
    try {
      parsed = yaml.load(yamlText);
    } catch (e: any) {
      setError(`YAML parse error: ${e.message}`);
      return;
    }

    // Accept either a top-level array or { archetypes: [...] }
    let items: any[] = [];
    if (Array.isArray(parsed)) {
      items = parsed;
    } else if (parsed?.archetypes && Array.isArray(parsed.archetypes)) {
      items = parsed.archetypes;
    } else {
      setError(
        'YAML must be an array of archetypes or an object with an "archetypes" key.',
      );
      return;
    }

    // Map snake_case keys to camelCase for the API
    const mapped = items.map((item: any) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      whenToSuggest: item.whenToSuggest || item.when_to_suggest,
      knowledgeNeeded: item.knowledgeNeeded || item.knowledge_needed,
      active: item.active !== undefined ? item.active : true,
    }));

    setYamlImporting(true);
    try {
      const headers = await authHeaders();
      const res = await api.post(
        '/suggestion-archetypes/bulk',
        { archetypes: mapped },
        { headers },
      );
      if (res.data.success) {
        const { results, errors: bulkErrors } = res.data;
        const created = results.filter(
          (r: any) => r.action === 'created',
        ).length;
        const updated = results.filter(
          (r: any) => r.action === 'updated',
        ).length;
        let msg = `Import complete: ${created} created, ${updated} updated.`;
        if (bulkErrors?.length) {
          msg += ` ${bulkErrors.length} errors.`;
        }
        setSuccessMsg(msg);
        if (bulkErrors?.length) {
          setError(
            bulkErrors.map((e: any) => `${e.id}: ${e.error}`).join('; '),
          );
        }
        setYamlText('');
        fetchList();
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Bulk import failed');
    } finally {
      setYamlImporting(false);
    }
  };

  // ─── Render: access check ─────────────────────────────────────────────

  if (hasAccess === false) {
    return (
      <div className='use-case-editor'>
        <div className='access-denied'>Access denied.</div>
      </div>
    );
  }

  // ─── Render: list view ────────────────────────────────────────────────

  if (!selectedId && !isNewMode) {
    return (
      <div className='use-case-editor'>
        <MobileNavToggle />
        <div className='header'>
          <h2>Suggestion Archetypes</h2>
          <div className='header-actions'>
            <label className='toggle-inactive'>
              <input
                type='checkbox'
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              Show inactive
            </label>
            <button className='primary-btn' onClick={startNew}>
              + New Archetype
            </button>
            <button
              className='secondary-btn'
              onClick={() => setShowYamlImport(!showYamlImport)}
            >
              {showYamlImport ? 'Hide YAML Import' : 'Import YAML'}
            </button>
          </div>
        </div>

        {error && <div className='msg error'>{error}</div>}
        {successMsg && <div className='msg success'>{successMsg}</div>}

        {showYamlImport && (
          <div className='edit-form' style={{ marginBottom: 24 }}>
            <div className='form-group'>
              <label>
                Paste YAML{' '}
                <span className='hint'>
                  (array of archetypes with id, title, description,
                  when_to_suggest, knowledge_needed)
                </span>
              </label>
              <textarea
                value={yamlText}
                onChange={(e) => setYamlText(e.target.value)}
                placeholder={`- id: suggest_grocery_order\n  title: Suggest Grocery Order\n  description: ...\n  when_to_suggest: ...\n  knowledge_needed: ...`}
                rows={12}
                style={{ fontFamily: "'Geist Mono', monospace", fontSize: 13 }}
              />
            </div>
            <div className='form-actions'>
              <button
                className='primary-btn'
                onClick={handleYamlImport}
                disabled={yamlImporting}
              >
                {yamlImporting ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        )}

        {listLoading ? (
          <div className='loading'>Loading...</div>
        ) : archetypes.length === 0 ? (
          <div className='empty'>No suggestion archetypes found.</div>
        ) : (
          <div className='use-case-list'>
            {archetypes.map((a) => (
              <div
                key={a.id}
                className={`use-case-card ${!a.active ? 'inactive' : ''}`}
                onClick={() => selectArchetype(a.id)}
              >
                <div className='card-header'>
                  <span className='card-title'>{a.title}</span>
                  {!a.active && (
                    <span className='badge inactive-badge'>Inactive</span>
                  )}
                </div>
                <div className='card-slug'>{a.id}</div>
                <div className='card-summary'>{a.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── Render: detail / edit view ───────────────────────────────────────

  return (
    <div className='use-case-editor'>
      <MobileNavToggle />
      <div className='header'>
        <button className='back-btn' onClick={backToList}>
          &larr; Back
        </button>
        <h2>{isNewMode ? 'New Archetype' : editTitle || 'Edit Archetype'}</h2>
      </div>

      {error && <div className='msg error'>{error}</div>}
      {successMsg && <div className='msg success'>{successMsg}</div>}

      {detailLoading ? (
        <div className='loading'>Loading...</div>
      ) : (
        <div className='edit-form'>
          {/* ID */}
          <div className='form-group'>
            <label>
              ID{' '}
              <span className='hint'>
                (lowercase letters, numbers, underscores)
              </span>
            </label>
            <input
              type='text'
              value={editId}
              onChange={(e) => setEditId(e.target.value)}
              placeholder='e.g. suggest_grocery_order'
              disabled={!isNewMode}
            />
          </div>

          {/* Active toggle */}
          <div className='form-group inline'>
            <label>
              <input
                type='checkbox'
                checked={editActive}
                onChange={(e) => setEditActive(e.target.checked)}
              />
              Active
            </label>
          </div>

          {/* Title */}
          <div className='form-group'>
            <label>Title</label>
            <input
              type='text'
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder='Human-readable title'
            />
          </div>

          {/* Description */}
          <div className='form-group'>
            <label>
              Description{' '}
              <span className='hint'>
                (one-liner overview of what this suggestion does)
              </span>
            </label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder='What this suggestion is about'
              rows={3}
            />
          </div>

          {/* When to Suggest */}
          <div className='form-group'>
            <label>
              When to Suggest{' '}
              <span className='hint'>
                (positive + negative signals for the specialist)
              </span>
            </label>
            <textarea
              value={editWhenToSuggest}
              onChange={(e) => setEditWhenToSuggest(e.target.value)}
              placeholder='When is this suggestion most/least worth making?'
              rows={8}
            />
          </div>

          {/* Knowledge Needed */}
          <div className='form-group'>
            <label>
              Knowledge Needed{' '}
              <span className='hint'>
                (what context is required to do this well)
              </span>
            </label>
            <textarea
              value={editKnowledgeNeeded}
              onChange={(e) => setEditKnowledgeNeeded(e.target.value)}
              placeholder='What does the specialist need to know to make this suggestion?'
              rows={8}
            />
          </div>

          {/* Actions */}
          <div className='form-actions'>
            <button
              className='primary-btn'
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : isNewMode ? 'Create' : 'Save Changes'}
            </button>
            {!isNewMode && (
              <button
                className='danger-btn'
                onClick={handleDeactivate}
                disabled={saving}
              >
                Deactivate
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestionArchetypeEditor;
